<template>
  <div class="page-container">
    <Sidebar />
    
    <div class="main-content">
      <div class="page-header">
        <h1>用户管理</h1>
      </div>
      
      <div class="content-body">
        <!-- Filter Bar -->
        <div class="filter-bar">
          <div class="filter-item">
            <label>角色筛选：</label>
            <select v-model="roleFilter" @change="changeFilter">
              <option value="">全部</option>
              <option value="user">普通用户</option>
              <option value="vip">VIP</option>
              <option value="admin">管理员</option>
            </select>
          </div>
          <button class="refresh-btn" @click="fetchUsers">刷新</button>
        </div>
        
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>
        <table class="user-table" v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>昵称</th>
              <th>公司名称</th>
              <th>用户类型</th>
              <th>手机号</th>
              <th>角色</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>{{ user.id }}</td>
              <td>{{ user.display_name || '-' }}</td>
              <td>{{ user.company_name || '-' }}</td>
              <td>{{ user.user_type || '-' }}</td>
              <td>{{ user.phone || '-' }}</td>
              <td>
                <select 
                  class="role-select" 
                  :value="user.role" 
                  @change="updateRole(user.id, $event.target.value)"
                >
                  <option value="user">普通用户</option>
                  <option value="vip">VIP</option>
                  <option value="admin">管理员</option>
                  <option value="guest">游客</option>
                </select>
              </td>
              <td>
                <StatusTag :status="user.is_active ? 'active' : 'inactive'" />
              </td>
              <td>{{ formatDate(user.created_at) }}</td>
              <td>
                <button class="action-btn detail" @click="showDetail(user)">详情</button>
                <button 
                  v-if="user.is_active" 
                  class="action-btn disable"
                  @click="toggleStatus(user.id, false)"
                >
                  禁用
                </button>
                <button 
                  v-else 
                  class="action-btn enable"
                  @click="toggleStatus(user.id, true)"
                >
                  启用
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="pagination" v-if="!loading && !error">
          <div class="pagination-left">
            <span>共 {{ total }} 条记录</span>
            <select class="page-size-select" v-model="pageSize" @change="changePageSize">
              <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}条/页</option>
            </select>
          </div>
          <div class="pagination-right">
            <button 
              class="page-btn" 
              :disabled="currentPage === 1" 
              @click="changePage(1)"
            >首页</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === 1" 
              @click="changePage(currentPage - 1)"
            >上一页</button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages" 
              @click="changePage(currentPage + 1)"
            >下一页</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages" 
              @click="changePage(totalPages)"
            >尾页</button>
            <span class="page-jump">
              到第 <input 
                type="number" 
                class="page-input" 
                v-model.number="jumpPage" 
                @keyup.enter="changePage(jumpPage)"
                min="1" 
                :max="totalPages"
              /> 页
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户详情弹窗 -->
    <div class="modal-overlay" v-if="showModal" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>用户详情</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body" v-if="selectedUser">
          <div class="detail-section">
            <h3>基础信息</h3>
            <div class="detail-row">
              <span class="label">用户ID：</span>
              <span class="value">{{ selectedUser.id }}</span>
            </div>
            <div class="detail-row">
              <span class="label">手机号：</span>
              <span class="value">{{ selectedUser.phone || '未绑定' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">角色：</span>
              <span class="value">{{ getRoleName(selectedUser.role) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">状态：</span>
              <span :class="['value', selectedUser.is_active ? 'status-active' : 'status-inactive']">
                {{ selectedUser.is_active ? '正常' : '禁用' }}
              </span>
            </div>
            <div class="detail-row">
              <span class="label">查询次数：</span>
              <span class="value">{{ selectedUser.query_count }}</span>
            </div>
            <div class="detail-row">
              <span class="label">创建时间：</span>
              <span class="value">{{ formatDate(selectedUser.created_at) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">OpenID：</span>
              <span class="value openid">{{ selectedUser.openid || '未绑定' }}</span>
            </div>
          </div>

          <div class="detail-section">
            <h3>完善信息（用户资料）</h3>
            <div class="detail-row">
              <span class="label">昵称：</span>
              <span class="value">{{ selectedUser.display_name || '未填写' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">公司名称：</span>
              <span class="value">{{ selectedUser.company_name || '未填写' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">用户类型：</span>
              <span class="value">{{ selectedUser.user_type || '未填写' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">个人简介：</span>
              <span class="value bio">{{ selectedUser.bio || '未填写' }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-close" @click="closeModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Sidebar from '../components/Sidebar.vue'
import StatusTag from '../components/StatusTag.vue'
import { formatDate } from '../utils/dateFormat'
import { getAdminPassword } from '../utils/api'

export default {
  name: 'UserList',
  components: { Sidebar, StatusTag },
  data() { 
    return { 
      users: [], 
      total: 0,
      loading: true, 
      error: '', 
      roleFilter: '',
      showModal: false,
      selectedUser: null,
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20],
      jumpPage: 1
    } 
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize) || 1
    }
  },
  mounted() { this.fetchUsers() },
  methods: {
    async fetchUsers() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }
        if (this.roleFilter) {
          params.role = this.roleFilter
        }
        const response = await axios.get('/api/admin/users', { 
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.users = response.data.data.list || []
          this.total = response.data.data.total || 0
        } else {
          this.error = response.data.msg
        }
      } catch (err) { 
        this.error = '网络错误'
        console.error('Fetch users error:', err)
      }
      finally { this.loading = false }
    },
    changeFilter() {
      this.currentPage = 1
      this.fetchUsers()
    },
    showDetail(user) {
      this.selectedUser = user
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.selectedUser = null
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.fetchUsers()
    },
    changePageSize() {
      this.currentPage = 1
      this.fetchUsers()
    },
    getRoleName(role) {
      const roleMap = {
        'admin': '管理员',
        'vip': 'VIP',
        'user': '普通用户',
        'guest': '游客'
      }
      return roleMap[role] || role
    },
    async toggleStatus(userId, isActive) {
      try {
        const response = await axios.put(`/api/admin/users/${userId}/status`, { is_active: isActive }, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          const user = this.users.find(u => u.id === userId)
          if (user) user.is_active = isActive
          if (this.selectedUser && this.selectedUser.id === userId) {
            this.selectedUser.is_active = isActive
          }
        } else {
          alert(response.data.msg || '操作失败')
        }
      } catch (err) {
        alert('操作失败')
        console.error('Toggle status error:', err)
      }
    },
    async updateRole(userId, newRole) {
      try {
        const response = await axios.put(`/api/admin/users/${userId}/role`, { role: newRole }, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          const user = this.users.find(u => u.id === userId)
          if (user) user.role = newRole
          if (this.selectedUser && this.selectedUser.id === userId) {
            this.selectedUser.role = newRole
          }
        } else {
          alert(response.data.msg || '更新角色失败')
          this.fetchUsers()
        }
      } catch (err) {
        alert('更新角色失败')
        console.error('Update role error:', err)
        this.fetchUsers()
      }
    },
    formatDate(dateStr) {
      return formatDate(dateStr)
    }
  }
}
</script>

<style scoped>
.page-container { 
  display: flex; 
  min-height: 100vh; 
  background: #f5f5f5; 
}

.main-content { 
  flex: 1; 
  padding: 30px; 
  margin-left: 260px;
}

.page-header { 
  margin-bottom: 20px; 
}

.page-header h1 { 
  font-size: 24px; 
  color: #2c3e50; 
}

.content-body { 
  background: white; 
  padding: 20px; 
  border-radius: 10px; 
  box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
}

.loading, .error { 
  text-align: center; 
  padding: 50px; 
  color: #666; 
}

.error { 
  color: #e74c3c; 
}

.filter-bar { 
  display: flex; 
  align-items: center; 
  gap: 20px; 
  margin-bottom: 20px; 
  padding: 15px; 
  background: #f8f9fa; 
  border-radius: 8px; 
}

.filter-item { 
  display: flex; 
  align-items: center; 
  gap: 10px; 
}

.filter-item label { 
  font-weight: 500; 
  color: #333; 
}

.filter-item select { 
  padding: 8px 12px; 
  border: 1px solid #ddd; 
  border-radius: 4px; 
  font-size: 14px; 
}

.refresh-btn { 
  padding: 8px 20px; 
  background: #3498db; 
  color: white; 
  border: none; 
  border-radius: 4px; 
  cursor: pointer; 
  font-size: 14px; 
}

.refresh-btn:hover { 
  background: #2980b9; 
}

.user-table { 
  width: 100%; 
  border-collapse: collapse; 
}

.user-table th { 
  background: #f8f9fa; 
  padding: 15px; 
  text-align: left; 
  font-weight: 600; 
  color: #333; 
  border-bottom: 2px solid #eee; 
}

.user-table td { 
  padding: 15px; 
  border-bottom: 1px solid #eee; 
  color: #666; 
}

.user-table tr:hover { 
  background: #f8f9fa; 
}

.status { 
  padding: 4px 12px; 
  border-radius: 4px; 
  font-size: 12px; 
}

.status.active { 
  background: #e8f5e9; 
  color: #4caf50; 
}

.status.inactive { 
  background: #ffebee; 
  color: #f44336; 
}

.role-select { 
  padding: 6px 10px; 
  border: 1px solid #ddd; 
  border-radius: 4px; 
  font-size: 13px; 
}

.action-btn { 
  padding: 6px 14px; 
  border: none; 
  border-radius: 4px; 
  cursor: pointer; 
  font-size: 12px; 
  margin-right: 5px; 
}

.action-btn.detail { 
  background: #3498db; 
  color: white; 
}

.action-btn.detail:hover { 
  background: #2980b9; 
}

.action-btn.enable { 
  background: #4caf50; 
  color: white; 
}

.action-btn.enable:hover { 
  background: #43a047; 
}

.action-btn.disable { 
  background: #f44336; 
  color: white; 
}

.action-btn.disable:hover { 
  background: #e53935; 
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 12px 0;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #666;
  font-size: 14px;
}

.page-size-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  padding: 6px 14px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #3498db;
  color: white;
  border-color: #3498db;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.page-jump {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-input {
  width: 50px;
  padding: 5px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 600px;
  max-width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  font-size: 18px;
  color: #2c3e50;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #e74c3c;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  max-height: calc(80vh - 140px);
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h3 {
  font-size: 16px;
  color: #3498db;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #3498db;
}

.detail-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.label {
  width: 120px;
  font-weight: 500;
  color: #333;
}

.value {
  flex: 1;
  color: #666;
}

.value.openid {
  font-size: 12px;
  color: #999;
  word-break: break-all;
}

.value.bio {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.status-active {
  color: #4caf50;
  font-weight: 500;
}

.status-inactive {
  color: #f44336;
  font-weight: 500;
}

.modal-footer {
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.btn-close {
  padding: 10px 24px;
  background: #666;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-close:hover {
  background: #555;
}
</style>